import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Package, Users, Mail, TrendingUp, RefreshCw, CheckCircle2, Star, EyeOff, Eye, Send, Camera, Trash2, Radio, MapPin, FileDown, MailCheck, Building2, FileSpreadsheet } from "lucide-react";
import { adminApi, API } from "../lib/api";
import { Logo } from "../components/layout/Navbar";
import { StarRating } from "../components/StarRating";
import ProofViewerModal from "../components/ProofViewerModal";
import LiveMap from "../components/LiveMap";
import BulkImportModal from "../components/BulkImportModal";
import BillyPanel from "../components/BillyPanel";
import BillyErrorsPanel from "../components/BillyErrorsPanel";
import AuditLogPanel from "../components/AuditLogPanel";
import EmployeeManagementPanel from "../components/admin/EmployeeManagementPanel";
import VagtplanPanel from "../components/admin/VagtplanPanel";
import EsgPanel from "../components/admin/EsgPanel";
import RoutesPanel from "../components/RoutesPanel";
import DriverStatementsPanel from "../components/DriverStatementsPanel";
import SubcontractorsPanel from "../components/SubcontractorsPanel";
import PendingDriversPanel from "../components/PendingDriversPanel";
import DriverPerformancePanel from "../components/DriverPerformancePanel";
import DriverTrainingPenaltiesPanel from "../components/DriverTrainingPenaltiesPanel";
import PenaltyInsightsPanel from "../components/PenaltyInsightsPanel";
import NewsletterPanel from "../components/NewsletterPanel";
import ReferralPanel from "../components/ReferralPanel";
import InboxPanel from "../components/InboxPanel";
import AnalyticsPanel from "../components/AnalyticsPanel";
import DriverStatsPanel from "../components/DriverStatsPanel";
import EmploymentOnboardingsPanel from "../components/EmploymentOnboardingsPanel";
import GdprPanel from "../components/GdprPanel";
import PakkeshopPanel from "../components/PakkeshopPanel";
import LogisticsPanel from "../components/admin/LogisticsPanel";
import B2BParcelsPanel from "../components/admin/B2BParcelsPanel";
import B2BRoutesPanel from "../components/admin/B2BRoutesPanel";
import HealthCheckWidget from "../components/admin/HealthCheckWidget";
import DriverApplicationDetailModal from "../components/DriverApplicationDetailModal";
import ContractSigningStatsWidget from "../components/ContractSigningStatsWidget";
import B2BAgreementsPanel from "../components/B2BAgreementsPanel";
import WhistleblowerPanel from "../components/WhistleblowerPanel";
import BackupStatusPanel from "../components/BackupStatusPanel";
import DriftDashboard from "../components/DriftDashboard";
import CustomersPanel from "../components/CustomersPanel";
import DriverProblemsPanel from "../components/DriverProblemsPanel";
import DriverChatPanel from "../components/DriverChatPanel";
import MailRoutingPanel from "../components/MailRoutingPanel";
import LegalReviewsPanel from "../components/LegalReviewsPanel";
import MorningCheckPanel from "../components/MorningCheckPanel";
import PilotStatusPanel from "../components/admin/PilotStatusPanel";
import AdminLiveRoutesPanel from "../components/admin/AdminLiveRoutesPanel";
import HubInboundPanel from "../components/admin/HubInboundPanel";
import HubCagesPanel from "../components/admin/HubCagesPanel";
import DistributionPlanningPanel from "../components/admin/DistributionPlanningPanel";
import AdminSearchBar from "../components/admin/AdminSearchBar";
import PerformanceDashboardPanel from "../components/admin/PerformanceDashboardPanel";
import FollowupCasesPanel from "../components/admin/FollowupCasesPanel";
import VognmandOnboardingAdminPanel from "../components/admin/VognmandOnboardingAdminPanel";
import TicketsPanel from "../components/TicketsPanel";

const STATUSES = ["new", "confirmed", "in_transit", "delivered", "cancelled"];
const STATUS_LABEL = { new: "Ny", confirmed: "Bekræftet", in_transit: "Undervejs", delivered: "Leveret", cancelled: "Annulleret" };

const StatusPill = ({ s }) => {
  const map = {
    new: "bg-slate-200 text-slate-800",
    confirmed: "bg-blue-100 text-blue-900",
    in_transit: "bg-amber-100 text-amber-900",
    delivered: "bg-emerald-100 text-emerald-900",
    cancelled: "bg-red-100 text-red-900",
  };
  return <span className={`pill-status ${map[s] || "bg-slate-100"}`}>{STATUS_LABEL[s] || s}</span>;
};

const PaymentPill = ({ s }) => {
  const map = { paid: "bg-emerald-100 text-emerald-900", pending: "bg-amber-100 text-amber-900", failed: "bg-red-100 text-red-900", expired: "bg-slate-200 text-slate-700" };
  return <span className={`pill-status ${map[s] || "bg-slate-100"}`}>{s}</span>;
};

export default function AdminDashboardPage() {
  // Default tab = "drift" så admin starter med Drift i dag oversigt
  // Kan overskrives med ?tab=xxx i URL'en (eksisterende behavior bevares længere nede)
  const [tab, setTab] = React.useState("morning");
  const [stats, setStats] = React.useState(null);
  const [bookings, setBookings] = React.useState([]);
  const [drivers, setDrivers] = React.useState([]);
  const [contacts, setContacts] = React.useState([]);
  const [reviews, setReviews] = React.useState([]);
  const [trustpilot, setTrustpilot] = React.useState(null);
  const [driverAccounts, setDriverAccounts] = React.useState([]);
  const [businessLeads, setBusinessLeads] = React.useState([]);
  const [businessCustomers, setBusinessCustomers] = React.useState([]);
  const [monthlyInvoices, setMonthlyInvoices] = React.useState([]);
  const [newCustomer, setNewCustomer] = React.useState({ company_name: "", cvr: "", contact_name: "", billing_email: "", billing_address: "", billing_zip: "", billing_city: "", contact_phone: "", tier: "starter", monthly_fee_dkk: 0, notes: "" });
  const [creatingCustomer, setCreatingCustomer] = React.useState(false);
  const [genState, setGenState] = React.useState({ customer_id: "", year: new Date().getFullYear(), month: new Date().getMonth() + 1, busy: false });
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [bulkDefaultCustomerId, setBulkDefaultCustomerId] = React.useState("");
  const [newDriver, setNewDriver] = React.useState({ name: "", email: "", phone: "", password: "", subcontractor_id: "" });
  const [subcontractors, setSubcontractors] = React.useState([]);
  const [creatingDriver, setCreatingDriver] = React.useState(false);
  const [proofBookingId, setProofBookingId] = React.useState(null);
  const [driverAppDetailId, setDriverAppDetailId] = React.useState(null);
  const [liveData, setLiveData] = React.useState(null);
  const [liveLoading, setLiveLoading] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const nav = useNavigate();
  const token = localStorage.getItem("ema_admin_token");

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const a = adminApi();
      const [s, b, d, c, r, tp, da, bl, bc, mi, sc] = await Promise.all([
        a.get("/admin/stats"),
        a.get("/admin/bookings"),
        a.get("/admin/drivers"),
        a.get("/admin/contacts"),
        a.get("/admin/reviews"),
        a.get("/admin/trustpilot/config"),
        a.get("/admin/drivers/accounts"),
        a.get("/admin/business-leads"),
        a.get("/admin/business-customers"),
        a.get("/admin/monthly-invoices"),
        a.get("/admin/subcontractors"),
      ]);
      setStats(s.data); setBookings(b.data); setDrivers(d.data); setContacts(c.data); setReviews(r.data); setTrustpilot(tp.data); setDriverAccounts(da.data); setBusinessLeads(bl.data); setBusinessCustomers(bc.data); setMonthlyInvoices(mi.data); setSubcontractors(sc.data || []);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("ema_admin_token"); nav("/admin/login"); }
      else toast.error("Kunne ikke hente data");
    } finally { setLoading(false); }
  };

  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Deep-link support: ?tab=legal or #legal → switch to that tab
  React.useEffect(() => {
    const fromHash = (window.location.hash || "").replace(/^#/, "").trim();
    const fromQuery = new URLSearchParams(window.location.search).get("tab");
    const target = (fromHash || fromQuery || "").toLowerCase();
    if (target) setTab(target);
  }, []);

  const loadLive = async () => {
    setLiveLoading(true);
    try {
      const { data } = await adminApi().get("/admin/live");
      setLiveData(data);
    } catch { toast.error("Kunne ikke hente live-data"); }
    finally { setLiveLoading(false); }
  };

  React.useEffect(() => {
    if (tab !== "live") return;
    loadLive();
    const t = setInterval(loadLive, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [tab]);

  if (!token) {
    // Preserve ?tab= / #hash so deep-links survive the login redirect
    const target = `/admin/login${window.location.search}${window.location.hash}`;
    return <Navigate to={target} replace />;
  }

  const updateStatus = async (id, status) => {
    try {
      await adminApi().patch(`/admin/bookings/${id}`, { booking_status: status });
      toast.success("Status opdateret");
      // Reload to pick up new timestamps from server
      load();
    } catch { toast.error("Kunne ikke opdatere"); }
  };

  const moderateReview = async (id, approved) => {
    try {
      await adminApi().patch(`/admin/reviews/${id}`, { approved });
      toast.success(approved ? "Anmeldelse synlig" : "Anmeldelse skjult");
      setReviews((rs) => rs.map((r) => r.id === id ? { ...r, approved } : r));
    } catch { toast.error("Kunne ikke opdatere"); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Slet denne anmeldelse permanent? Dette kan ikke fortrydes.")) return;
    try {
      await adminApi().delete(`/admin/reviews/${id}`);
      toast.success("Anmeldelse slettet");
      setReviews((rs) => rs.filter((r) => r.id !== id));
    } catch { toast.error("Kunne ikke slette"); }
  };

  const deleteBooking = async (b) => {
    if (!window.confirm(`Slet booking ${b.booking_number} permanent?\n\nDette sletter også tilknyttede betalinger, sporing og påmindelser. Kan IKKE fortrydes.`)) return;
    try {
      await adminApi().delete(`/admin/bookings/${b.id}`);
      toast.success(`Booking ${b.booking_number} slettet`);
      setBookings((xs) => xs.filter((x) => x.id !== b.id));
      load();
    } catch { toast.error("Kunne ikke slette booking"); }
  };

  const deleteContact = async (c) => {
    if (!window.confirm(`Slet besked fra ${c.name || c.email}?`)) return;
    try {
      await adminApi().delete(`/admin/contacts/${c.id}`);
      toast.success("Besked slettet");
      setContacts((xs) => xs.filter((x) => x.id !== c.id));
    } catch { toast.error("Kunne ikke slette"); }
  };

  const deleteDriverAccount = async (d) => {
    if (!window.confirm(`Slet chauffør ${d.name} (${d.email}) permanent?\n\nDette fjerner også deres GPS-historik og push-tokens. Kan IKKE fortrydes.`)) return;
    try {
      await adminApi().delete(`/admin/drivers/${d.id}`);
      toast.success("Chauffør slettet");
      setDriverAccounts((xs) => xs.filter((x) => x.id !== d.id));
      setDrivers((xs) => xs.filter((x) => x.id !== d.id));
    } catch { toast.error("Kunne ikke slette chauffør"); }
  };

  const resetDriverOnboarding = async (d) => {
    if (!window.confirm(`Nulstil onboarding for ${d.name} (${d.email})?\n\nDette giver chaufføren 2 nye forsøg og fjerner evt. lockout.`)) return;
    try {
      await adminApi().post(`/admin/drivers/${d.id}/onboarding/reset`);
      toast.success("Onboarding nulstillet");
      // refresh driver accounts list
      const a = adminApi();
      const r = await a.get("/admin/drivers/accounts");
      setDriverAccounts(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke nulstille"); }
  };

  const deleteBusinessLead = async (l) => {
    if (!window.confirm(`Slet erhvervs-lead fra ${l.company_name || l.contact_name}?`)) return;
    try {
      await adminApi().delete(`/admin/business-leads/${l.id}`);
      toast.success("Lead slettet");
      setBusinessLeads((xs) => xs.filter((x) => x.id !== l.id));
    } catch { toast.error("Kunne ikke slette lead"); }
  };

  const resendDriverWelcome = async (d) => {
    if (!window.confirm(`Send velkomst-email igen til ${d.name} (${d.email})?\n\nDette genererer et NYT midlertidigt password og sender det på email. Chaufførens gamle password vil ikke længere virke.`)) return;
    try {
      const { data } = await adminApi().post(`/admin/drivers/accounts/${d.id}/resend-welcome`);
      if (data?.email_sent) {
        toast.success(`Velkomst-email sendt til ${data.to_email}`);
      } else {
        toast.error("SMTP fejlede — tjek backend-logs");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke gensende"); }
  };

  const deleteDriverApplication = async (d) => {
    if (!window.confirm(`Slet chauffør-ansøgning fra ${d.full_name || d.email}?\n\nUploaded dokumenter (kørekort, straffeattest, CV) slettes også. Kan IKKE fortrydes.`)) return;
    try {
      await adminApi().delete(`/admin/driver-applications/${d.id}`);
      toast.success("Ansøgning slettet");
      setDrivers((xs) => xs.filter((x) => x.id !== d.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke slette ansøgning"); }
  };

  const hardDeleteBusinessCustomer = async (c) => {
    if (!window.confirm(`SLET ${c.company_name} PERMANENT?\n\nAlle månedsfakturaer for denne kunde slettes også. Bookings bevares men frakobles kunden.\n\nKan IKKE fortrydes.`)) return;
    try {
      await adminApi().delete(`/admin/business-customers/${c.id}?hard=true`);
      toast.success("Erhvervskunde slettet permanent");
      setBusinessCustomers((xs) => xs.filter((x) => x.id !== c.id));
      // Refresh monthly invoices since some may have been deleted
      try {
        const r = await adminApi().get("/admin/monthly-invoices");
        setMonthlyInvoices(r.data || []);
      } catch { /* ignore */ }
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke slette kunde"); }
  };

  const deleteMonthlyInvoice = async (mi) => {
    if (!window.confirm(`Slet månedsfaktura ${mi.invoice_number} permanent?\n\nKan IKKE fortrydes.`)) return;
    try {
      await adminApi().delete(`/admin/monthly-invoices/${mi.id}`);
      toast.success("Månedsfaktura slettet");
      setMonthlyInvoices((xs) => xs.filter((x) => x.id !== mi.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke slette faktura"); }
  };

  const resetTestData = async () => {
    const confirm1 = window.prompt(
      "⚠️ NULSTIL ALT TEST-DATA ⚠️\n\n" +
      "Dette sletter ALLE bookings, betalinger, chauffører, kontaktbeskeder, leads, anmeldelser, rabatkoder og GPS-historik.\n\n" +
      "Admin-login og system-config bevares.\n\n" +
      "Skriv 'NULSTIL' for at bekræfte:"
    );
    if (confirm1 !== "NULSTIL") {
      toast.error("Annulleret");
      return;
    }
    try {
      const { data } = await adminApi().post("/admin/reset-test-data", { confirm: "NULSTIL" });
      toast.success(`✅ ${data.total} dokumenter slettet`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke nulstille");
    }
  };

  const resendInvoice = async (b) => {
    try {
      const { data } = await adminApi().post(`/admin/bookings/${b.id}/resend-invoice`);
      toast.success(`Faktura sendt til ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende faktura");
    }
  };

  const sendTrustpilotInvite = (b) => {
    if (!trustpilot?.afs_email) { toast.error("Trustpilot ikke konfigureret"); return; }
    const subject = `Tak for din booking hos EMA Kurer (${b.booking_number})`;
    const body = `Kære ${b.customer_name}\n\nTak for din booking hos EMA Kurer ApS — vi håber du var tilfreds med leveringen.\n\nVi vil meget gerne høre din mening — du modtager kort en email fra Trustpilot, hvor du kan dele din oplevelse.\n\nMed venlig hilsen\nEMA Kurer ApS\nkontakt@emakurer.dk\nCVR 46465717`;
    const mailto = `mailto:${encodeURIComponent(b.customer_email)}?bcc=${encodeURIComponent(trustpilot.afs_email)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    adminApi().post(`/admin/trustpilot/mark-invited/${b.id}`).catch(() => {});
    setBookings((bs) => bs.map((x) => x.id === b.id ? { ...x, trustpilot_invited_at: new Date().toISOString() } : x));
    toast.success("Email-klient åbnet med Trustpilot BCC");
  };

  const createDriver = async (e) => {
    e.preventDefault();
    if (!newDriver.name || !newDriver.email || !newDriver.password) {
      toast.error("Udfyld navn, email og password"); return;
    }
    if (newDriver.password.length < 6) { toast.error("Password skal være mindst 6 tegn"); return; }
    setCreatingDriver(true);
    try {
      await adminApi().post("/admin/drivers/accounts", newDriver);
      toast.success(`Chauffør ${newDriver.name} oprettet`);
      setNewDriver({ name: "", email: "", phone: "", password: "", subcontractor_id: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oprette chauffør");
    } finally { setCreatingDriver(false); }
  };

  const toggleDriver = async (id, active) => {
    try {
      await adminApi().patch(`/admin/drivers/accounts/${id}?active=${active}`);
      toast.success(active ? "Chauffør aktiveret" : "Chauffør deaktiveret");
      setDriverAccounts((arr) => arr.map((d) => d.id === id ? { ...d, active } : d));
    } catch { toast.error("Kunne ikke opdatere"); }
  };

  const updateLeadStatus = async (id, status) => {
    try {
      await adminApi().patch(`/admin/business-leads/${id}`, { status });
      toast.success("Lead opdateret");
      setBusinessLeads((ls) => ls.map((l) => l.id === id ? { ...l, status } : l));
    } catch { toast.error("Kunne ikke opdatere"); }
  };

  const createBusinessCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomer.company_name || !newCustomer.contact_name || !newCustomer.billing_email) {
      toast.error("Udfyld virksomhedsnavn, kontaktperson og faktura-email"); return;
    }
    setCreatingCustomer(true);
    try {
      await adminApi().post("/admin/business-customers", newCustomer);
      toast.success(`${newCustomer.company_name} oprettet`);
      setNewCustomer({ company_name: "", cvr: "", contact_name: "", billing_email: "", billing_address: "", billing_zip: "", billing_city: "", contact_phone: "", tier: "starter", monthly_fee_dkk: 0, notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oprette kunde");
    } finally { setCreatingCustomer(false); }
  };

  const generateMonthlyInvoice = async () => {
    if (!genState.customer_id) { toast.error("Vælg en erhvervskunde"); return; }
    setGenState((g) => ({ ...g, busy: true }));
    try {
      const { data } = await adminApi().post("/admin/monthly-invoices/generate", {
        customer_id: genState.customer_id,
        year: Number(genState.year),
        month: Number(genState.month),
        send_email: true,
      });
      toast.success(`Faktura ${data.invoice_number} (${data.booking_count} bookings · ${data.total_dkk} kr) genereret · email ${data.email_status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke generere faktura");
    } finally { setGenState((g) => ({ ...g, busy: false })); }
  };

  const markInvoicePaid = async (id) => {
    try {
      await adminApi().post(`/admin/monthly-invoices/${id}/mark-paid`);
      toast.success("Markeret som betalt");
      setMonthlyInvoices((ms) => ms.map((m) => m.id === id ? { ...m, status: "paid", paid_at: new Date().toISOString() } : m));
    } catch { toast.error("Kunne ikke opdatere"); }
  };

  const deactivateCustomer = async (id) => {
    if (!window.confirm("Deaktivér denne erhvervskunde? Eksisterende fakturaer påvirkes ikke.")) return;
    try {
      await adminApi().delete(`/admin/business-customers/${id}`);
      toast.success("Kunde deaktiveret");
      setBusinessCustomers((cs) => cs.map((c) => c.id === id ? { ...c, active: false } : c));
    } catch { toast.error("Kunne ikke deaktivere"); }
  };

  const logout = () => { localStorage.removeItem("ema_admin_token"); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-brand-bg2" data-testid="admin-dashboard">
      <header className="bg-brand-dark text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Logo light />
          <div className="flex items-center gap-3">
            <AdminSearchBar />
            <button onClick={load} data-testid="refresh-btn" className="text-white/70 hover:text-white" title="Refresh"><RefreshCw size={18} /></button>
            <button
              onClick={resetTestData}
              data-testid="reset-test-data-btn"
              className="hidden sm:inline-flex items-center gap-2 border border-red-400/50 text-red-200 hover:bg-red-500/10 py-2 px-3 text-xs uppercase tracking-widest font-bold"
              title="Slet alle test-data (bookings, chauffører, kontaktbeskeder, etc.) — admin-login bevares"
            >
              <Trash2 size={12} /> Nulstil test-data
            </button>
            <button
              onClick={async () => {
                const token = localStorage.getItem("ema_admin_token");
                try {
                  const res = await fetch(`${API}/admin/sales/pitch-deck`, { headers: { Authorization: `Bearer ${token}` } });
                  if (!res.ok) throw new Error();
                  const blob = await res.blob();
                  const u = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = u; a.download = "EMA_Kurer_B2B_Salgspraesentation.pptx";
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(u);
                  toast.success("Salgspræsentation downloadet");
                } catch { toast.error("Kunne ikke hente præsentation"); }
              }}
              data-testid="download-pitch-deck-btn"
              className="hidden sm:inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 py-2 px-3 text-xs uppercase tracking-widest font-bold"
              title="Download B2B salgspræsentation (PowerPoint) til kundemøder"
            >
              <FileSpreadsheet size={12} /> Salgs-PPT
            </button>
            <button
              onClick={async () => {
                const token = localStorage.getItem("ema_admin_token");
                try {
                  const res = await fetch(`${API}/admin/sales/demo-video`, { headers: { Authorization: `Bearer ${token}` } });
                  if (!res.ok) throw new Error();
                  const blob = await res.blob();
                  const u = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = u; a.download = "EMA_Kurer_Demo_Video.mp4";
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(u);
                  toast.success("Demo-video downloadet");
                } catch { toast.error("Kunne ikke hente video"); }
              }}
              data-testid="download-demo-video-btn"
              className="hidden sm:inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 py-2 px-3 text-xs uppercase tracking-widest font-bold"
              title="Download B2B demo-video (90 sek MP4) — kunde-rejse fra start til slut"
            >
              <Radio size={12} /> Demo-video
            </button>
            <button onClick={logout} data-testid="logout-btn" className="btn-ghost-light py-2 px-4 text-sm"><LogOut size={14} /> Log ud</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-8">
        <div className="kicker">Dashboard</div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-brand-dark mt-2 tracking-tight">Oversigt</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 mt-8" data-testid="admin-stats">
          {stats && [
            { i: Package, l: "Bookinger", v: stats.total_bookings },
            { i: CheckCircle2, l: "Betalt", v: stats.paid },
            { i: TrendingUp, l: "Omsætning", v: `${stats.revenue_dkk} kr` },
            { i: Package, l: "Leveret", v: stats.delivered },
            { i: Users, l: "Chauffører", v: stats.drivers },
            { i: Mail, l: "Beskeder", v: stats.contacts },
            { i: Building2, l: "B2B leads", v: `${stats.business_leads || 0}${stats.business_leads_new ? ` · ${stats.business_leads_new} nye` : ""}` },
            { i: Star, l: "Anmeldelser", v: `${stats.reviews} · ${stats.avg_rating || "–"}` },
          ].map((s) => (
            <div key={s.l} className="bg-white border border-brand p-4">
              <div className="flex items-center justify-between text-brand-text2"><s.i size={16} /></div>
              <div className="font-display font-bold text-2xl text-brand-dark mt-2">{s.v}</div>
              <div className="text-xs uppercase tracking-widest text-brand-text2 mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="relative mt-10 border-b border-brand">
          <div className="flex gap-1 overflow-x-auto scrollbar-thin pb-[1px]"
               style={{ scrollbarWidth: "thin" }}>
            {[
            { k: "morning", t: "Morgentjek" },
            { k: "health", t: "System-status" },
            { k: "pilot_status", t: "Pilot Status" },
            { k: "drift", t: "Drift i dag" },
            { k: "live_routes", t: "Live Ruter" },
            { k: "hub_inbound", t: "HUB Inbound" },
            { k: "distribution", t: "Distribution" },
            { k: "hub_cages", t: "Bure" },
            { k: "perf_dashboard", t: "Performance ⭐" },
            { k: "followup", t: "Opfølgning" },
            { k: "vognmand_compliance", t: "Vognmand Compliance" },
            { k: "inbox", t: "Inbox" },
            { k: "tickets", t: "Tickets" },
            { k: "driver_chat", t: "Chauffør-chat" },
            { k: "driver_problems", t: "Problem-rapporter" },
            { k: "customers", t: "Kunder" },
            { k: "mail_routing", t: "Mail-routing" },
            { k: "legal", t: "Lovsider" },
            { k: "analytics", t: "Analytics" },
            { k: "live", t: "Live Kort" },
            { k: "bookings", t: `Bookinger (${bookings.length})` },
            { k: "drivers", t: `Ansøgninger (${drivers.length})` },
            { k: "driver_accounts", t: `Chauffør-konti (${driverAccounts.length})` },
            { k: "driver_stats", t: "Chauffør-stats" },
            { k: "employment", t: "Ansættelser" },
            { k: "pakkeshop", t: "Pakkeshop" },
            { k: "logistics", t: "Logistik" },
            { k: "b2b_parcels", t: "B2B Pakker" },
            { k: "b2b_routes", t: "B2B Ruter" },
            { k: "gdpr", t: "GDPR" },
            { k: "contacts", t: `Beskeder (${contacts.length})` },
            { k: "business_leads", t: `B2B Leads (${businessLeads.length})` },
            { k: "business_customers", t: `Erhvervskunder (${businessCustomers.length})` },
            { k: "reviews", t: `Anmeldelser (${reviews.length})` },
            { k: "billy", t: "Billy / SKAT" },
            { k: "routes", t: "Ruter" },
            { k: "subcontractors", t: "Vognmænd" },
            { k: "pending_drivers", t: "Godkendelser" },
            { k: "performance", t: "Performance" },
            { k: "training", t: "Træning & Bøder" },
            { k: "penalty_insights", t: "Bøde-rapport" },
            { k: "newsletter", t: "Nyhedsbrev" },
            { k: "referrals", t: "Henvisninger" },
            { k: "b2b", t: "B2B aftaler" },
            { k: "opgorelse", t: "Opgørelse" },
            { k: "whistleblower", t: "Whistleblower" },
            { k: "backup", t: "Backup" },
            { k: "audit", t: "Audit log" },
            { k: "employees", t: "Medarbejdere" },
            { k: "vagtplan", t: "Vagtplan" },
            { k: "esg", t: "ESG / CO2e" },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${tab === t.k ? "border-brand-red text-brand-red" : "border-transparent text-brand-text2 hover:text-brand-dark"}`}>
              {t.t}
            </button>
          ))}
          </div>
          {/* Right-edge gradient hint to indicate horizontal scroll */}
          <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-brand-bg2 via-brand-bg2/70 to-transparent"></div>
        </div>

        <div className="mt-6">
          {loading && <div className="text-brand-text2">Indlæser...</div>}

          {!loading && tab === "inbox" && (
            <InboxPanel onTabChange={setTab} />
          )}

          {!loading && tab === "pilot_status" && (
            <PilotStatusPanel />
          )}

          {!loading && tab === "live_routes" && (
            <AdminLiveRoutesPanel />
          )}

          {!loading && tab === "hub_inbound" && (
            <HubInboundPanel />
          )}

          {!loading && tab === "distribution" && (
            <DistributionPlanningPanel />
          )}

          {!loading && tab === "hub_cages" && (
            <HubCagesPanel />
          )}

          {!loading && tab === "perf_dashboard" && (
            <PerformanceDashboardPanel />
          )}

          {!loading && tab === "followup" && (
            <FollowupCasesPanel />
          )}

          {!loading && tab === "vognmand_compliance" && (
            <VognmandOnboardingAdminPanel />
          )}

          {!loading && tab === "tickets" && (
            <TicketsPanel />
          )}

          {!loading && tab === "customers" && (
            <CustomersPanel />
          )}

          {!loading && tab === "driver_chat" && (
            <DriverChatPanel />
          )}

          {!loading && tab === "driver_problems" && (
            <DriverProblemsPanel />
          )}

          {!loading && tab === "mail_routing" && (
            <MailRoutingPanel />
          )}

          {!loading && tab === "legal" && (
            <LegalReviewsPanel />
          )}

          {!loading && tab === "analytics" && (
            <AnalyticsPanel token={localStorage.getItem("ema_admin_token")} />
          )}

          {!loading && tab === "driver_stats" && (
            <DriverStatsPanel token={localStorage.getItem("ema_admin_token")} />
          )}

          {!loading && tab === "employment" && (
            <EmploymentOnboardingsPanel token={localStorage.getItem("ema_admin_token")} />
          )}

          {!loading && tab === "pakkeshop" && (
            <PakkeshopPanel token={localStorage.getItem("ema_admin_token")} />
          )}

          {!loading && tab === "logistics" && (
            <LogisticsPanel />
          )}

          {!loading && tab === "b2b_parcels" && (
            <B2BParcelsPanel />
          )}

          {!loading && tab === "b2b_routes" && (
            <B2BRoutesPanel />
          )}

          {!loading && tab === "health" && (
            <HealthCheckWidget />
          )}

          {!loading && tab === "routes" && (
            <RoutesPanel />
          )}

          {!loading && tab === "gdpr" && (
            <GdprPanel token={localStorage.getItem("ema_admin_token")} />
          )}

          {!loading && tab === "live" && (
            <div data-testid="live-tab">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                {liveData ? [
                  { l: "Online", v: liveData.stats?.drivers_online ?? 0, c: "text-emerald-700", b: "border-emerald-300" },
                  { l: "Chauffører", v: liveData.stats?.drivers_total ?? 0 },
                  { l: "Undervejs", v: liveData.stats?.active_deliveries ?? 0, c: "text-amber-700", b: "border-amber-300" },
                  { l: "Klar", v: liveData.stats?.ready_for_pickup ?? 0 },
                ].map((s) => (
                  <div key={s.l} className={`bg-white border ${s.b || "border-brand"} p-4`}>
                    <div className={`font-display font-bold text-2xl ${s.c || "text-brand-dark"}`}>{s.v}</div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-text2 mt-1">{s.l}</div>
                  </div>
                )) : <div className="col-span-4 bg-white border border-brand p-6 text-brand-text2">{liveLoading ? "Henter live-data..." : "Klik 'Live Kort' fanen for at indlæse"}</div>}
              </div>
              <div className="bg-white border border-brand p-2 mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 px-2">
                  <Radio size={14} className="text-emerald-600 animate-pulse" />
                  <span className="text-xs font-semibold text-brand-dark">Live · auto-opdaterer hvert 15. sek</span>
                </div>
                <button onClick={loadLive} className="btn-secondary py-1.5 px-3 text-xs" data-testid="live-refresh">
                  <RefreshCw size={12} className={liveLoading ? "animate-spin" : ""} /> Opdatér
                </button>
              </div>
              <LiveMap drivers={liveData?.drivers || []} height={520} />
              <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <div className="kicker">Chauffører</div>
                  <h3 className="font-display font-semibold text-lg text-brand-dark mt-1 mb-3">Aktive på vej</h3>
                  <div className="space-y-2" data-testid="live-drivers">
                    {(liveData?.drivers || []).length === 0 && <div className="bg-white border border-brand p-5 text-sm text-brand-text2">Ingen chauffør-konti aktive</div>}
                    {(liveData?.drivers || []).map((d) => (
                      <div key={d.id} className="bg-white border border-brand p-4" data-testid={`live-driver-${d.email}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${d.is_online ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
                              <span className="font-display font-semibold text-brand-dark">{d.name}</span>
                            </div>
                            <div className="text-xs text-brand-text2 mt-0.5">{d.email}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-display font-bold text-brand-dark">{d.active_bookings?.length || 0}</div>
                            <div className="text-[10px] uppercase tracking-widest text-brand-text2">Leveringer</div>
                          </div>
                        </div>
                        {d.location && (
                          <div className="text-xs text-brand-text2 mt-2 flex items-center gap-2 font-mono">
                            <MapPin size={11} /> {d.location.lat.toFixed(5)}, {d.location.lng.toFixed(5)} · {new Date(d.location.at).toLocaleTimeString("da-DK")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="kicker">Leveringer</div>
                  <h3 className="font-display font-semibold text-lg text-brand-dark mt-1 mb-3">Igangværende</h3>
                  <div className="space-y-2" data-testid="live-bookings">
                    {(liveData?.drivers || []).flatMap((d) => (d.active_bookings || []).map((b) => ({ ...b, driver_name: d.name }))).concat(liveData?.unassigned_bookings || []).map((b) => (
                      <div key={b.id} className="bg-white border border-brand p-3 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono font-bold text-xs">{b.booking_number}</span>
                          <StatusPill s={b.booking_status} />
                        </div>
                        <div className="text-xs text-brand-text2 mt-1">
                          {b.pickup_zip} {b.pickup_city} → {b.delivery_zip} {b.delivery_city}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-brand-text2 mt-1">
                          {b.driver_name ? `Chauffør: ${b.driver_name}` : "Ikke tildelt"} · {b.pickup_time}
                        </div>
                      </div>
                    ))}
                    {(!liveData || (liveData.drivers || []).every((d) => !d.active_bookings?.length) && (!liveData.unassigned_bookings?.length)) && (
                      <div className="bg-white border border-brand p-5 text-sm text-brand-text2">Ingen aktive leveringer lige nu</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && tab === "bookings" && (
            <div className="bg-white border border-brand overflow-x-auto" data-testid="bookings-table">
              <table className="w-full text-sm">
                <thead className="bg-brand-bg2 text-brand-text2 uppercase tracking-wider text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">Booking</th>
                    <th className="text-left px-4 py-3">Kunde</th>
                    <th className="text-left px-4 py-3">Rute</th>
                    <th className="text-left px-4 py-3">Tid</th>
                    <th className="text-left px-4 py-3">Beløb</th>
                    <th className="text-left px-4 py-3">Betaling</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-brand-text2">Ingen bookinger endnu</td></tr>}
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-brand" data-testid={`booking-${b.booking_number}`}>
                      <td className="px-4 py-3 font-mono text-xs">{b.booking_number}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-brand-dark">{b.customer_name}</div>
                        <div className="text-xs text-brand-text2">{b.customer_email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div>{b.pickup_zip} {b.pickup_city}</div>
                        <div className="text-brand-text2">→ {b.delivery_zip} {b.delivery_city}</div>
                        {b.distance_km && <div className="text-[10px] text-brand-text2 mt-0.5">{b.distance_km} km</div>}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="font-semibold">{b.pickup_time}</div>
                        <div className="text-[10px] uppercase tracking-widest text-brand-red mt-0.5">{b.delivery_type === "same_day" ? "Same-Day" : b.delivery_type === "express" ? "Express" : b.delivery_type === "evening" ? "Aften" : b.delivery_type === "business" ? "Erhverv" : b.delivery_type || "—"}</div>
                      </td>
                      <td className="px-4 py-3 font-semibold">{b.total_amount_dkk} kr</td>
                      <td className="px-4 py-3"><PaymentPill s={b.payment_status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <select value={b.booking_status} onChange={(e) => updateStatus(b.id, e.target.value)} className="input-flat py-1.5 text-xs" data-testid={`status-${b.booking_number}`}>
                            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                          </select>
                          {b.booking_status === "delivered" && b.delivered_with_proof && (
                            <button
                              onClick={() => setProofBookingId(b.id)}
                              title="Vis bevis-billeder"
                              data-testid={`proof-${b.booking_number}`}
                              className="p-1.5 text-xs border border-brand text-brand-red hover:border-brand-red"
                            >
                              <Camera size={12} />
                            </button>
                          )}
                          {b.payment_status === "paid" && (
                            <>
                              <a
                                href={`${API}/invoice/${b.id}?email=${encodeURIComponent(b.customer_email)}`}
                                target="_blank"
                                rel="noreferrer"
                                title="Download faktura PDF"
                                data-testid={`invoice-${b.booking_number}`}
                                className="p-1.5 text-xs border border-brand text-brand-dark hover:border-brand-dark"
                              >
                                <FileDown size={12} />
                              </a>
                              <button
                                onClick={() => resendInvoice(b)}
                                title={b.invoice_sent_at ? `Send igen (sidst sendt ${new Date(b.invoice_sent_at).toLocaleString("da-DK")})` : "Send faktura via e-mail"}
                                data-testid={`resend-invoice-${b.booking_number}`}
                                className={`p-1.5 text-xs border ${b.invoice_sent_at ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-brand text-brand-dark hover:border-brand-dark"}`}
                              >
                                <MailCheck size={12} />
                              </button>
                            </>
                          )}
                          {b.booking_status === "delivered" && trustpilot?.enabled && (
                            <button
                              onClick={() => sendTrustpilotInvite(b)}
                              title={b.trustpilot_invited_at ? "Allerede inviteret — send igen" : "Send Trustpilot invitation"}
                              data-testid={`trustpilot-${b.booking_number}`}
                              className={`p-1.5 text-xs border ${b.trustpilot_invited_at ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-brand text-brand-dark hover:border-brand-dark"}`}
                            >
                              <Send size={12} />
                            </button>
                          )}
                          <button
                            onClick={() => deleteBooking(b)}
                            title="Slet booking permanent"
                            data-testid={`delete-booking-${b.booking_number}`}
                            className="p-1.5 text-xs border border-red-300 text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === "drivers" && (
            <div className="space-y-4" data-testid="drivers-tab">
              <ContractSigningStatsWidget onOpenApplication={(id) => setDriverAppDetailId(id)} />
              <div className="bg-white border border-brand overflow-x-auto" data-testid="drivers-table">
              <table className="w-full text-sm">
                <thead className="bg-brand-bg2 text-brand-text2 uppercase tracking-wider text-xs">
                  <tr><th className="text-left px-4 py-3">Navn</th><th className="text-left px-4 py-3">Kontakt</th><th className="text-left px-4 py-3">By</th><th className="text-left px-4 py-3">Kørekort / Bil</th><th className="text-left px-4 py-3">Modtaget</th><th className="text-right px-4 py-3"></th></tr>
                </thead>
                <tbody>
                  {drivers.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-brand-text2">Ingen ansøgninger</td></tr>}
                  {drivers.map((d) => {
                    const docs = Array.isArray(d.documents) ? d.documents.map((x) => x?.kind).filter(Boolean) : [];
                    const required = ["drivers_license_front", "drivers_license_back", "criminal_record"];
                    if (d.is_danish_citizen) required.push("passport"); else required.push("work_permit");
                    const missing = required.filter((k) => !docs.includes(k));
                    const missingLabels = {
                      drivers_license_front: "Kørekort F",
                      drivers_license_back: "Kørekort B",
                      criminal_record: "Straffeattest",
                      passport: "Pas",
                      work_permit: "Opholdst.",
                    };
                    const ONB_BADGE = {
                      not_sent: null,
                      sent: { cls: "bg-sky-100 text-sky-900 border-sky-300", label: "Onb. sendt" },
                      opened: { cls: "bg-violet-100 text-violet-900 border-violet-300", label: "Mail åbnet" },
                      clicked: { cls: "bg-amber-100 text-amber-900 border-amber-300", label: "Link åbnet" },
                      completed: { cls: "bg-emerald-100 text-emerald-900 border-emerald-300", label: "Onb. udfyldt" },
                    };
                    const onbBadge = ONB_BADGE[d.onboarding_status] || null;
                    return (
                    <tr
                      key={d.id}
                      className="border-t border-brand hover:bg-brand-bg2/50 cursor-pointer"
                      onClick={() => setDriverAppDetailId(d.id)}
                      data-testid={`driver-app-row-${d.id}`}
                    >
                      <td className="px-4 py-3 font-semibold underline-offset-2 hover:underline">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{d.full_name}</span>
                          {missing.length > 0 && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 font-semibold uppercase tracking-wide"
                              title={`Mangler: ${missing.map((k) => missingLabels[k] || k).join(", ")}`}
                              data-testid={`missing-badge-${d.id}`}
                            >
                              Mangler {missing.length}
                            </span>
                          )}
                          {onbBadge && d.status !== "rejected" && (
                            <span
                              className={`text-[10px] px-1.5 py-0.5 border font-semibold uppercase tracking-wide ${onbBadge.cls}`}
                              data-testid={`onb-badge-${d.id}`}
                            >
                              {onbBadge.label}
                            </span>
                          )}
                          {d.status === "rejected" && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 border font-semibold uppercase tracking-wide bg-slate-200 text-slate-800 border-slate-400"
                              data-testid={`rejected-badge-${d.id}`}
                            >
                              Afvist
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs"><div>{d.email}</div><div className="text-brand-text2">{d.phone}</div></td>
                      <td className="px-4 py-3">{d.city}</td>
                      <td className="px-4 py-3 text-xs">{d.has_drivers_license ? "Kørekort ✓" : "Intet kørekort"} · {d.has_own_vehicle ? "Egen bil ✓" : "Ingen bil"}</td>
                      <td className="px-4 py-3 text-xs text-brand-text2">{new Date(d.created_at).toLocaleString("da-DK")}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setDriverAppDetailId(d.id)}
                          className="px-2 py-1 mr-2 text-xs border border-brand hover:bg-brand-bg2"
                          data-testid={`view-driver-app-${d.id}`}
                          title="Se ansøgnings-indhold og dokumenter"
                        >
                          Vis
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm(`Send onboarding-link til ${d.email}?`)) return;
                            try {
                              const { data } = await adminApi().post(`/admin/driver-applications/${d.id}/send-onboarding-link`);
                              if (data?.ok) {
                                alert(`✓ Onboarding-link sendt til ${d.email}.\n\nLink (gyldigt 14 dage):\n${data.link}`);
                              } else {
                                alert("Fejl: " + (data?.detail || "ukendt"));
                              }
                            } catch (e) {
                              alert("Fejl: " + (e?.response?.data?.detail || e.message));
                            }
                          }}
                          className="px-2 py-1 mr-2 text-xs bg-brand-dark text-white hover:bg-brand-red"
                          data-testid={`send-onb-link-${d.id}`}
                          title="Send onboarding-link til chaufføren"
                        >
                          → Onboarding
                        </button>
                        <button
                          onClick={() => deleteDriverApplication(d)}
                          className="p-1.5 border border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`delete-driver-app-${d.id}`}
                          title="Slet ansøgning permanent"
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {!loading && tab === "b2b" && (
            <B2BAgreementsPanel />
          )}


          {!loading && tab === "contacts" && (
            <div className="bg-white border border-brand overflow-x-auto" data-testid="contacts-table">
              <table className="w-full text-sm">
                <thead className="bg-brand-bg2 text-brand-text2 uppercase tracking-wider text-xs">
                  <tr><th className="text-left px-4 py-3">Fra</th><th className="text-left px-4 py-3">Emne</th><th className="text-left px-4 py-3">Besked</th><th className="text-left px-4 py-3">Modtaget</th><th className="text-right px-4 py-3"></th></tr>
                </thead>
                <tbody>
                  {contacts.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-brand-text2">Ingen beskeder</td></tr>}
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-t border-brand align-top">
                      <td className="px-4 py-3"><div className="font-semibold">{c.name}</div><div className="text-xs text-brand-text2">{c.email}</div></td>
                      <td className="px-4 py-3 font-semibold">{c.subject}</td>
                      <td className="px-4 py-3 text-xs max-w-md whitespace-pre-wrap">{c.message}</td>
                      <td className="px-4 py-3 text-xs text-brand-text2">{new Date(c.created_at).toLocaleString("da-DK")}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => deleteContact(c)}
                          className="p-1.5 border border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`delete-contact-${c.id}`}
                          title="Slet besked"
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === "business_leads" && (
            <div className="bg-white border border-brand overflow-x-auto" data-testid="business-leads-table">
              <table className="w-full text-sm">
                <thead className="bg-brand-bg2 text-brand-text2 uppercase tracking-wider text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">Firma</th>
                    <th className="text-left px-4 py-3">Kontakt</th>
                    <th className="text-left px-4 py-3">Branche / Volumen</th>
                    <th className="text-left px-4 py-3">Pakke</th>
                    <th className="text-left px-4 py-3">Besked</th>
                    <th className="text-left px-4 py-3">Modtaget</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {businessLeads.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-brand-text2">Ingen B2B-leads endnu — del /erhverv-linket i jeres marketing.</td></tr>}
                  {businessLeads.map((l) => (
                    <tr key={l.id} className="border-t border-brand align-top" data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-brand-dark">{l.company_name}</div>
                        {l.cvr && <div className="text-xs text-brand-text2">CVR {l.cvr}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{l.contact_name}</div>
                        <div className="text-xs"><a href={`mailto:${l.contact_email}`} className="text-brand-red">{l.contact_email}</a></div>
                        <div className="text-xs text-brand-text2"><a href={`tel:${l.contact_phone}`}>{l.contact_phone}</a></div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="capitalize">{l.industry || "—"}</div>
                        <div className="text-brand-text2">{l.expected_volume || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold capitalize">{l.tier_interest || "—"}</td>
                      <td className="px-4 py-3 text-xs max-w-xs whitespace-pre-wrap">{l.message || <span className="text-brand-text2/60">—</span>}</td>
                      <td className="px-4 py-3 text-xs text-brand-text2 whitespace-nowrap">{new Date(l.created_at).toLocaleString("da-DK")}</td>
                      <td className="px-4 py-3">
                        <select
                          value={l.status || "new"}
                          onChange={(e) => updateLeadStatus(l.id, e.target.value)}
                          className="text-xs border border-brand px-2 py-1.5 bg-white"
                          data-testid={`lead-status-${l.id}`}
                        >
                          <option value="new">Ny</option>
                          <option value="contacted">Kontaktet</option>
                          <option value="qualified">Kvalificeret</option>
                          <option value="won">Vundet</option>
                          <option value="lost">Tabt</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => deleteBusinessLead(l)}
                          className="p-1.5 border border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`delete-business-lead-${l.id}`}
                          title="Slet lead permanent"
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === "driver_accounts" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="driver-accounts">
              <form onSubmit={createDriver} className="lg:col-span-5 bg-white border border-brand p-6" data-testid="create-driver-form">
                <div className="kicker">Opret chauffør</div>
                <h3 className="font-display font-semibold text-xl text-brand-dark mt-2">Ny chauffør-konto</h3>
                <div className="space-y-4 mt-5">
                  <div>
                    <label className="label-flat">Navn *</label>
                    <input className="input-flat" value={newDriver.name} onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} data-testid="new-driver-name" required />
                  </div>
                  <div>
                    <label className="label-flat">Email *</label>
                    <input type="email" className="input-flat" value={newDriver.email} onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value.toLowerCase() })} data-testid="new-driver-email" required />
                  </div>
                  <div>
                    <label className="label-flat">Telefon</label>
                    <input className="input-flat" value={newDriver.phone} onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} data-testid="new-driver-phone" />
                  </div>
                  <div>
                    <label className="label-flat">Vognmand (valgfri)</label>
                    <select
                      className="input-flat"
                      value={newDriver.subcontractor_id || ""}
                      onChange={(e) => setNewDriver({ ...newDriver, subcontractor_id: e.target.value || null })}
                      data-testid="new-driver-subcontractor"
                    >
                      <option value="">— Ingen (egen ansat) —</option>
                      {subcontractors.map((sc) => (
                        <option key={sc.id} value={sc.id}>{sc.company_name}</option>
                      ))}
                    </select>
                    {subcontractors.length === 0 && <div className="text-xs text-brand-text2 mt-1">Opret vognmænd under "Vognmænd"-tabben.</div>}
                  </div>
                  <div>
                    <label className="label-flat">Password * (min. 6 tegn)</label>
                    <input type="text" className="input-flat font-mono" value={newDriver.password} onChange={(e) => setNewDriver({ ...newDriver, password: e.target.value })} data-testid="new-driver-password" minLength={6} required />
                    <div className="text-xs text-brand-text2 mt-1">Skriv et password ned — chaufføren kan logge ind på <code>/driver/login</code> med det.</div>
                  </div>
                  <button type="submit" className="btn-primary w-full justify-center" disabled={creatingDriver} data-testid="create-driver-submit">
                    {creatingDriver ? "Opretter..." : "Opret chauffør"}
                  </button>
                </div>
              </form>

              <div className="lg:col-span-7">
                <div className="kicker">Eksisterende konti</div>
                <h3 className="font-display font-semibold text-xl text-brand-dark mt-2 mb-4">Aktive chauffører</h3>
                <div className="space-y-3">
                  {driverAccounts.length === 0 && <div className="bg-white border border-brand p-6 text-center text-brand-text2">Ingen chauffør-konti endnu</div>}
                  {driverAccounts.map((d) => (
                    <div key={d.id} className={`bg-white border ${d.active ? "border-brand" : "border-slate-200 opacity-60"} p-4 flex items-center justify-between gap-3`} data-testid={`driver-acct-${d.email}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-brand-dark">{d.name}</div>
                        <div className="text-xs text-brand-text2">{d.email} {d.phone && `· ${d.phone}`}</div>
                        <div className="text-[10px] uppercase tracking-widest mt-1 text-brand-text2 flex flex-wrap gap-2 items-center">
                          <span>Oprettet {new Date(d.created_at).toLocaleDateString("da-DK")}</span>
                          {d.onboarding_locked && <span className="bg-red-100 text-red-700 px-2 py-0.5 not-italic font-bold tracking-normal" data-testid={`onb-locked-${d.email}`}>Låst</span>}
                          {!d.onboarding_locked && d.onboarding_required && !d.onboarding_passed && (
                            <span className="bg-amber-100 text-amber-700 px-2 py-0.5 not-italic font-bold tracking-normal">Onboarding · {d.onboarding_attempts || 0}/2</span>
                          )}
                          {d.onboarding_passed && <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 not-italic font-bold tracking-normal">Bestået</span>}
                        </div>
                      </div>
                      {(d.onboarding_locked || (d.onboarding_required && !d.onboarding_passed)) && (
                        <button
                          onClick={() => resetDriverOnboarding(d)}
                          className="btn-secondary py-1.5 px-3 text-xs"
                          data-testid={`reset-onboarding-${d.email}`}
                          title="Nulstil onboarding-forsøg"
                        >
                          Nulstil onb.
                        </button>
                      )}
                      <button
                        onClick={() => resendDriverWelcome(d)}
                        className="btn-secondary py-1.5 px-3 text-xs inline-flex items-center gap-1"
                        data-testid={`resend-welcome-${d.email}`}
                        title="Send velkomst-email med login-info igen (nyt password)"
                      >
                        <Mail size={12} /> Send velkomst
                      </button>
                      {d.onboarding_passed && (
                        <button
                          onClick={() => {
                            adminApi().get(`/admin/drivers/${d.id}/onboarding/certificate`, { responseType: "blob" })
                              .then((r) => { const url = URL.createObjectURL(r.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); })
                              .catch(() => toast.error("Kunne ikke hente certifikat"));
                          }}
                          className="btn-secondary py-1.5 px-3 text-xs"
                          data-testid={`download-cert-${d.email}`}
                          title="Download onboarding-certifikat"
                        >
                          Certifikat
                        </button>
                      )}
                      <button
                        onClick={() => toggleDriver(d.id, !d.active)}
                        className={`btn-secondary py-1.5 px-3 text-xs ${d.active ? "" : "bg-brand-red text-white border-brand-red"}`}
                        data-testid={`toggle-driver-${d.email}`}
                      >
                        {d.active ? "Deaktivér" : "Aktivér"}
                      </button>
                      <button
                        onClick={() => deleteDriverAccount(d)}
                        className="p-2 border border-red-300 text-red-700 hover:bg-red-50"
                        data-testid={`delete-driver-${d.email}`}
                        title="Slet chauffør permanent"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!loading && tab === "business_customers" && (
            <div className="space-y-8" data-testid="business-customers-tab">
              {/* Create customer */}
              <div className="bg-white border border-brand p-6">
                <div className="kicker">Ny erhvervskunde</div>
                <h3 className="font-display font-semibold text-xl text-brand-dark mt-2">Opret B2B-kunde til månedsfakturering</h3>
                <form onSubmit={createBusinessCustomer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
                  <input className="input-flat" placeholder="Virksomhedsnavn *" value={newCustomer.company_name} onChange={(e) => setNewCustomer({ ...newCustomer, company_name: e.target.value })} data-testid="bc-company" />
                  <input className="input-flat" placeholder="CVR" value={newCustomer.cvr} onChange={(e) => setNewCustomer({ ...newCustomer, cvr: e.target.value })} data-testid="bc-cvr" />
                  <input className="input-flat" placeholder="Kontaktperson *" value={newCustomer.contact_name} onChange={(e) => setNewCustomer({ ...newCustomer, contact_name: e.target.value })} data-testid="bc-contact" />
                  <input className="input-flat" type="email" placeholder="Faktura-email *" value={newCustomer.billing_email} onChange={(e) => setNewCustomer({ ...newCustomer, billing_email: e.target.value })} data-testid="bc-email" />
                  <input className="input-flat" placeholder="Telefon" value={newCustomer.contact_phone} onChange={(e) => setNewCustomer({ ...newCustomer, contact_phone: e.target.value })} data-testid="bc-phone" />
                  <select className="input-flat" value={newCustomer.tier} onChange={(e) => setNewCustomer({ ...newCustomer, tier: e.target.value })} data-testid="bc-tier">
                    <option value="starter">Starter (1.999 kr/md)</option>
                    <option value="business">Business (4.499 kr/md)</option>
                    <option value="premium">Premium (8.999 kr/md)</option>
                    <option value="custom">Skræddersyet</option>
                  </select>
                  <input className="input-flat sm:col-span-2 lg:col-span-1" placeholder="Faktura-adresse" value={newCustomer.billing_address} onChange={(e) => setNewCustomer({ ...newCustomer, billing_address: e.target.value })} data-testid="bc-address" />
                  <input className="input-flat" placeholder="Postnr." value={newCustomer.billing_zip} onChange={(e) => setNewCustomer({ ...newCustomer, billing_zip: e.target.value })} data-testid="bc-zip" />
                  <input className="input-flat" placeholder="By" value={newCustomer.billing_city} onChange={(e) => setNewCustomer({ ...newCustomer, billing_city: e.target.value })} data-testid="bc-city" />
                  <textarea className="input-flat sm:col-span-2 lg:col-span-3" rows={2} placeholder="Notater (intern)" value={newCustomer.notes} onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })} data-testid="bc-notes" />
                  <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
                    <button type="submit" disabled={creatingCustomer} className="btn-primary" data-testid="bc-submit">
                      {creatingCustomer ? "Opretter..." : "Opret kunde"}
                    </button>
                  </div>
                </form>
              </div>

              {/* Generate monthly invoice */}
              <div className="bg-brand-dark text-white p-6">
                <div className="kicker text-brand-red">Månedsfakturering</div>
                <h3 className="font-display font-semibold text-xl mt-2">Generér månedsfaktura</h3>
                <p className="text-white/60 text-sm mt-1">Aggregér alle betalte bookings fra én erhvervskunde i én måned til én PDF-faktura. Sendes automatisk til faktura-email.</p>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-5">
                  <select className="input-flat-dark sm:col-span-2" value={genState.customer_id} onChange={(e) => setGenState({ ...genState, customer_id: e.target.value })} data-testid="gen-customer">
                    <option value="">— Vælg kunde —</option>
                    {businessCustomers.filter((c) => c.active !== false).map((c) => (
                      <option key={c.id} value={c.id}>{c.company_name} ({c.billing_email})</option>
                    ))}
                  </select>
                  <select className="input-flat-dark" value={genState.year} onChange={(e) => setGenState({ ...genState, year: e.target.value })} data-testid="gen-year">
                    {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <select className="input-flat-dark" value={genState.month} onChange={(e) => setGenState({ ...genState, month: e.target.value })} data-testid="gen-month">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>{["Jan", "Feb", "Mar", "Apr", "Maj", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"][m - 1]}</option>
                    ))}
                  </select>
                </div>
                <button onClick={generateMonthlyInvoice} disabled={genState.busy || !genState.customer_id} className="btn-primary mt-4 disabled:opacity-50" data-testid="gen-submit">
                  {genState.busy ? "Genererer..." : "Generér faktura + send email"}
                </button>
                <button onClick={() => { setBulkDefaultCustomerId(genState.customer_id); setBulkOpen(true); }} className="ml-3 inline-flex items-center gap-2 px-5 py-3 bg-white/10 hover:bg-white/15 text-white font-semibold text-sm border border-white/20 transition" data-testid="bulk-open-btn">
                  Bulk-import bookings
                </button>
              </div>

              {/* Customers list */}
              <div className="bg-white border border-brand overflow-x-auto" data-testid="business-customers-table">
                <table className="w-full text-sm">
                  <thead className="bg-brand-bg2 border-b border-brand">
                    <tr><th className="text-left px-4 py-3 font-semibold">Virksomhed</th><th className="text-left px-4 py-3 font-semibold">CVR</th><th className="text-left px-4 py-3 font-semibold">Kontakt</th><th className="text-left px-4 py-3 font-semibold">Tier</th><th className="text-left px-4 py-3 font-semibold">Status</th><th className="px-4 py-3"></th></tr>
                  </thead>
                  <tbody>
                    {businessCustomers.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-brand-text2">Ingen erhvervskunder endnu — opret den første ovenfor.</td></tr>}
                    {businessCustomers.map((c) => (
                      <tr key={c.id} className={`border-b border-brand last:border-0 ${c.active === false ? "opacity-50" : ""}`} data-testid={`bc-row-${c.id}`}>
                        <td className="px-4 py-3"><div className="font-semibold text-brand-dark">{c.company_name}</div><div className="text-xs text-brand-text2">{c.billing_email}</div></td>
                        <td className="px-4 py-3 font-mono text-xs">{c.cvr || "—"}</td>
                        <td className="px-4 py-3"><div>{c.contact_name}</div><div className="text-xs text-brand-text2">{c.contact_phone || "—"}</div></td>
                        <td className="px-4 py-3 uppercase text-xs tracking-widest">{c.tier}</td>
                        <td className="px-4 py-3">{c.active === false ? <span className="pill-status bg-slate-200 text-slate-700">Inaktiv</span> : <span className="pill-status bg-emerald-100 text-emerald-900">Aktiv</span>}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="flex justify-end gap-3 items-center">
                            {c.active !== false && (
                              <>
                                <button onClick={() => { setBulkDefaultCustomerId(c.id); setBulkOpen(true); }} className="text-xs text-brand-red hover:underline" data-testid={`bc-bulk-${c.id}`}>Bulk import</button>
                                <button onClick={() => deactivateCustomer(c.id)} className="text-xs text-amber-700 hover:underline" data-testid={`bc-deactivate-${c.id}`}>Deaktivér</button>
                              </>
                            )}
                            <button
                              onClick={() => hardDeleteBusinessCustomer(c)}
                              className="p-1.5 border border-red-300 text-red-700 hover:bg-red-50"
                              data-testid={`bc-hard-delete-${c.id}`}
                              title="Slet kunde permanent (inkl. månedsfakturaer)"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Monthly invoices */}
              <div>
                <h3 className="font-display font-semibold text-xl text-brand-dark mb-3">Genererede månedsfakturaer ({monthlyInvoices.length})</h3>
                <div className="bg-white border border-brand overflow-x-auto" data-testid="monthly-invoices-table">
                  <table className="w-full text-sm">
                    <thead className="bg-brand-bg2 border-b border-brand">
                      <tr><th className="text-left px-4 py-3 font-semibold">Faktura</th><th className="text-left px-4 py-3 font-semibold">Kunde</th><th className="text-left px-4 py-3 font-semibold">Periode</th><th className="text-left px-4 py-3 font-semibold">Bookings</th><th className="text-left px-4 py-3 font-semibold">Beløb</th><th className="text-left px-4 py-3 font-semibold">Status</th><th className="px-4 py-3"></th></tr>
                    </thead>
                    <tbody>
                      {monthlyInvoices.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-brand-text2">Endnu ingen genererede månedsfakturaer.</td></tr>}
                      {monthlyInvoices.map((mi) => (
                        <tr key={mi.id} className="border-b border-brand last:border-0" data-testid={`mi-row-${mi.id}`}>
                          <td className="px-4 py-3 font-mono text-xs font-semibold text-brand-dark">{mi.invoice_number}</td>
                          <td className="px-4 py-3"><div>{mi.company_name}</div><div className="text-xs text-brand-text2">{mi.billing_email}</div></td>
                          <td className="px-4 py-3">{mi.period_label}</td>
                          <td className="px-4 py-3">{mi.booking_count}</td>
                          <td className="px-4 py-3 font-semibold">{Number(mi.total_dkk).toLocaleString("da-DK", { minimumFractionDigits: 2 })} kr</td>
                          <td className="px-4 py-3">
                            <span className={`pill-status ${mi.status === "paid" ? "bg-emerald-100 text-emerald-900" : mi.status === "sent" ? "bg-blue-100 text-blue-900" : "bg-slate-200 text-slate-700"}`}>{mi.status}</span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap flex gap-2 justify-end">
                            <a href={`${API}/admin/monthly-invoices/${mi.id}/pdf`} target="_blank" rel="noreferrer" className="text-brand-red hover:underline text-xs" data-testid={`mi-pdf-${mi.id}`} onClick={(e) => {
                              // Use admin token in URL via fetch then download? Simpler: open in new tab requires the token in header which doesn't work for href.
                              e.preventDefault();
                              const t = localStorage.getItem("ema_admin_token");
                              fetch(`${API}/admin/monthly-invoices/${mi.id}/pdf`, { headers: { Authorization: `Bearer ${t}` } })
                                .then((r) => r.blob())
                                .then((b) => {
                                  const u = URL.createObjectURL(b);
                                  const a = document.createElement("a");
                                  a.href = u; a.download = `${mi.invoice_number}.pdf`;
                                  document.body.appendChild(a); a.click(); a.remove();
                                  setTimeout(() => URL.revokeObjectURL(u), 1500);
                                })
                                .catch(() => toast.error("Kunne ikke hente PDF"));
                            }}>PDF</a>
                            {mi.status !== "paid" && (
                              <button onClick={() => markInvoicePaid(mi.id)} className="text-emerald-700 hover:underline text-xs" data-testid={`mi-paid-${mi.id}`}>Marker betalt</button>
                            )}
                            <button
                              onClick={() => deleteMonthlyInvoice(mi)}
                              className="p-1 border border-red-300 text-red-700 hover:bg-red-50"
                              data-testid={`mi-delete-${mi.id}`}
                              title="Slet månedsfaktura permanent"
                            >
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {!loading && tab === "reviews" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
              {reviews.length === 0 && <div className="md:col-span-2 lg:col-span-3 bg-white border border-brand p-10 text-center text-brand-text2">Ingen anmeldelser endnu</div>}
              {reviews.map((r) => (
                <div key={r.id} className={`bg-white border ${r.approved ? "border-brand" : "border-amber-300 bg-amber-50/40"} p-5 flex flex-col`} data-testid={`admin-review-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <StarRating value={r.rating} />
                    <span className="text-[10px] uppercase tracking-widest text-brand-text2">{new Date(r.created_at).toLocaleDateString("da-DK")}</span>
                  </div>
                  <div className="font-display font-semibold text-brand-dark mt-3">{r.title}</div>
                  <p className="text-sm text-brand-text2 mt-2 flex-1">{r.body}</p>
                  <div className="border-t border-brand mt-4 pt-3 flex items-center justify-between text-xs">
                    <div>
                      <div className="font-semibold text-brand-dark">{r.display_name}</div>
                      <div className="text-brand-text2 font-mono mt-0.5">{r.booking_number}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => moderateReview(r.id, !r.approved)}
                        className={`btn-secondary py-1.5 px-3 text-xs ${r.approved ? "" : "bg-brand-red text-white border-brand-red hover:bg-brand-red"}`}
                        data-testid={`moderate-${r.id}`}
                      >
                        {r.approved ? <><EyeOff size={12} /> Skjul</> : <><Eye size={12} /> Vis</>}
                      </button>
                      <button
                        onClick={() => deleteReview(r.id)}
                        className="btn-secondary py-1.5 px-2 text-xs border-red-300 text-red-700 hover:bg-red-50"
                        data-testid={`delete-review-${r.id}`}
                        title="Slet anmeldelse permanent"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && tab === "billy" && (
            <BillyPanel />
          )}

          {!loading && tab === "routes" && (
            <RoutesPanel />
          )}

          {!loading && tab === "subcontractors" && (
            <SubcontractorsPanel />
          )}

          {!loading && tab === "pending_drivers" && (
            <PendingDriversPanel />
          )}

          {!loading && tab === "performance" && (
            <DriverPerformancePanel />
          )}

          {!loading && tab === "training" && (
            <DriverTrainingPenaltiesPanel />
          )}

          {!loading && tab === "penalty_insights" && (
            <PenaltyInsightsPanel />
          )}

          {!loading && tab === "newsletter" && (
            <NewsletterPanel />
          )}

          {!loading && tab === "referrals" && (
            <ReferralPanel />
          )}

          {!loading && tab === "opgorelse" && (
            <DriverStatementsPanel />
          )}

          {!loading && tab === "audit" && (
            <AuditLogPanel />
          )}

          {!loading && tab === "employees" && (
            <EmployeeManagementPanel />
          )}

          {!loading && tab === "vagtplan" && (
            <VagtplanPanel />
          )}

          {!loading && tab === "esg" && (
            <EsgPanel />
          )}

          {!loading && tab === "whistleblower" && (
            <WhistleblowerPanel />
          )}

          {!loading && tab === "backup" && (
            <BackupStatusPanel />
          )}

          {!loading && tab === "morning" && (
            <MorningCheckPanel onJumpToTab={(t) => setTab(t)} />
          )}

          {!loading && tab === "drift" && (
            <DriftDashboard onJumpToTab={(t) => setTab(t)} />
          )}
        </div>
      </div>
      {proofBookingId && <ProofViewerModal bookingId={proofBookingId} onClose={() => setProofBookingId(null)} />}
      {driverAppDetailId && (
        <DriverApplicationDetailModal
          appId={driverAppDetailId}
          onClose={() => setDriverAppDetailId(null)}
        />
      )}
      <BulkImportModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        customers={businessCustomers}
        defaultCustomerId={bulkDefaultCustomerId}
        onComplete={() => load()}
      />
    </div>
  );
}
